import React, { useState, useEffect } from 'react';
import {
  Loader2, Trophy, CheckCircle, XCircle, TrendingUp
} from 'lucide-react';

const LeaderboardTab = ({ API_URL, getToken, leaderboardData, setLeaderboardData, leaderboardOptIn, setLeaderboardOptIn, school }) => {
  const [loading, setLoading] = useState(!leaderboardData);

  useEffect(() => {
    if (leaderboardData) return;
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/school/leaderboard/comparison`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (res.ok) setLeaderboardData(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [API_URL, getToken, leaderboardData, setLeaderboardData]);

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="leaderboard-tab">
      <div className="text-center">
        <Loader2 size={24} className="animate-spin mx-auto mb-3" style={{ color: '#F5831F' }} />
        <p className="text-xs" style={{ color: '#999' }}>Loading leaderboard data...</p>
      </div>
    </div>
  );

  if (!leaderboardData) return (
    <div className="text-center py-20" data-testid="leaderboard-tab">
      <p style={{ color: '#999' }}>Unable to load leaderboard</p>
    </div>
  );

  return (
    <div data-testid="leaderboard-tab">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-bold" style={{ color: '#1A1209' }}>School Comparison Leaderboard</h1>
        <span className="text-[10px] px-2.5 py-1 rounded-full font-semibold" style={{ backgroundColor: '#F5831F15', color: '#F5831F' }}>
          {leaderboardData.total_schools} schools competing
        </span>
      </div>
      <p className="text-xs mb-6" style={{ color: '#999' }}>
        See how your school compares to others across the HiBFF platform. Rankings are anonymized for non-participating schools.
      </p>

      {/* My Position Hero Card */}
      {leaderboardData.my_rank && (
        <div className="rounded-2xl p-6 mb-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1A1209 0%, #3D2E1A 100%)' }} data-testid="my-rank-card">
          <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10" style={{ background: '#F5831F', filter: 'blur(60px)', transform: 'translate(20%, -30%)' }} />
          <div className="flex items-center justify-between relative z-10">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#A89880' }}>Your Position</p>
              <div className="flex items-baseline gap-3">
                <span className="text-5xl font-black" style={{ color: '#F5831F' }}>#{leaderboardData.my_rank}</span>
                <span className="text-lg font-medium" style={{ color: '#E8DDD0' }}>of {leaderboardData.total_schools} schools</span>
              </div>
              <p className="text-xs mt-2" style={{ color: '#A89880' }}>{school?.school_name || 'Your School'}</p>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {leaderboardData.leaderboard?.find(e => e.is_mine) && (() => {
                const m = leaderboardData.leaderboard.find(e => e.is_mine).metrics;
                return [
                  { label: 'Sessions/Student', value: m.sessions_per_student },
                  { label: 'Active Rate', value: `${m.active_rate}%` },
                  { label: 'Avg Mood', value: `${m.avg_mood}/5` },
                ].map(s => (
                  <div key={s.label} className="text-center">
                    <p className="text-lg font-bold" style={{ color: '#F5831F' }}>{s.value}</p>
                    <p className="text-[9px] uppercase tracking-wider" style={{ color: '#A89880' }}>{s.label}</p>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Category Rankings */}
      {leaderboardData.categories && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {Object.entries(leaderboardData.categories).map(([id, cat]) => {
            const isTop3 = cat.my_rank <= 3;
            const isTop1 = cat.my_rank === 1;
            return (
              <div key={id} className="rounded-xl p-4 text-center" style={{
                backgroundColor: isTop1 ? '#FFFBEB' : isTop3 ? '#F0FDF4' : '#F8F7F4',
                border: `1px solid ${isTop1 ? '#FDE68A' : isTop3 ? '#D1FAE5' : '#E8E8E8'}`,
              }} data-testid={`category-${id}`}>
                <p className="text-[9px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#999' }}>{cat.label}</p>
                <p className="text-2xl font-black" style={{ color: isTop1 ? '#F59E0B' : isTop3 ? '#10B981' : '#1A1209' }}>
                  #{cat.my_rank}
                </p>
                <p className="text-[10px] font-medium" style={{ color: '#666' }}>
                  {cat.my_value} {cat.unit}
                </p>
                <p className="text-[9px] mt-1" style={{ color: '#CCC' }}>of {cat.total_schools}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Overall Rankings Table */}
      <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E8E8E8' }} data-testid="rankings-table">
        <div className="px-5 py-3 flex items-center justify-between" style={{ backgroundColor: '#F8F7F4', borderBottom: '1px solid #E8E8E8' }}>
          <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Overall Rankings</h3>
          <button
            onClick={async () => {
              const newOpt = !leaderboardOptIn;
              setLeaderboardOptIn(newOpt);
              await fetch(`${API_URL}/api/school/leaderboard/opt-in`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                body: JSON.stringify({ opt_in: newOpt }),
              });
            }}
            className="px-3 py-1.5 rounded-lg text-[10px] font-semibold flex items-center gap-1.5"
            style={{ backgroundColor: leaderboardOptIn ? '#ECFDF5' : '#F0F0F0', color: leaderboardOptIn ? '#059669' : '#999' }}
            data-testid="leaderboard-opt-in-btn"
          >
            {leaderboardOptIn ? <CheckCircle size={10} /> : <XCircle size={10} />}
            {leaderboardOptIn ? 'Name Visible' : 'Anonymous'}
          </button>
        </div>
        <table className="w-full">
          <thead>
            <tr style={{ borderBottom: '1px solid #E8E8E8' }}>
              {['Rank', 'School', 'Students', 'Sessions/Student', 'Active Rate', 'Challenges', 'Mood', 'Score'].map(h => (
                <th key={h} className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#999' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leaderboardData.leaderboard?.map((entry, i) => {
              const medalColors = { 1: '#F59E0B', 2: '#9CA3AF', 3: '#CD7F32' };
              const medalBg = { 1: '#FFFBEB', 2: '#F9FAFB', 3: '#FFF7ED' };
              const isMedal = entry.rank <= 3;
              return (
                <tr key={i} className={entry.is_mine ? '' : 'hover:bg-gray-50'}
                  style={{
                    borderBottom: '1px solid #F0F0F0',
                    backgroundColor: entry.is_mine ? '#F5831F08' : isMedal ? medalBg[entry.rank] : undefined,
                    borderLeft: entry.is_mine ? '3px solid #F5831F' : undefined,
                  }} data-testid={`lb-row-${entry.rank}`}>
                  <td className="px-4 py-3">
                    {isMedal ? (
                      <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black text-white" style={{ backgroundColor: medalColors[entry.rank] }}>{entry.rank}</span>
                    ) : (
                      <span className="text-sm font-semibold pl-1.5" style={{ color: '#999' }}>{entry.rank}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold" style={{ color: entry.is_mine ? '#F5831F' : '#1A1209' }}>{entry.display_name}</span>
                      {entry.is_mine && <span className="px-1.5 py-0.5 rounded text-[8px] font-black uppercase" style={{ backgroundColor: '#F5831F', color: 'white' }}>YOU</span>}
                    </div>
                    {entry.state && <p className="text-[9px]" style={{ color: '#CCC' }}>{entry.state}</p>}
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: '#666' }}>{entry.student_count}</td>
                  <td className="px-4 py-3"><span className="text-sm font-bold" style={{ color: '#1A1209' }}>{entry.metrics.sessions_per_student}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#E8E8E8' }}>
                        <div className="h-full rounded-full" style={{ width: `${entry.metrics.active_rate}%`, backgroundColor: entry.metrics.active_rate >= 80 ? '#10B981' : entry.metrics.active_rate >= 50 ? '#F59E0B' : '#DC2626' }} />
                      </div>
                      <span className="text-[10px] font-medium" style={{ color: '#666' }}>{entry.metrics.active_rate}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: '#666' }}>{entry.metrics.challenges_per_student}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{
                      backgroundColor: entry.metrics.avg_mood >= 4 ? '#ECFDF5' : entry.metrics.avg_mood >= 3 ? '#EFF6FF' : '#FEF3C7',
                      color: entry.metrics.avg_mood >= 4 ? '#059669' : entry.metrics.avg_mood >= 3 ? '#2563EB' : '#92400E',
                    }}>{entry.metrics.avg_mood}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-black" style={{ color: entry.is_mine ? '#F5831F' : '#1A1209' }}>{entry.composite_score}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Category Top 3 Podiums */}
      <h3 className="text-sm font-bold mt-6 mb-3" style={{ color: '#1A1209' }}>Category Podiums</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {leaderboardData.categories && Object.entries(leaderboardData.categories).map(([id, cat]) => (
          <div key={id} className="bg-white rounded-xl p-4" style={{ border: '1px solid #E8E8E8' }} data-testid={`podium-${id}`}>
            <h4 className="text-xs font-bold mb-3 flex items-center gap-1.5" style={{ color: '#1A1209' }}>
              <Trophy size={12} style={{ color: '#F59E0B' }} /> {cat.label}
            </h4>
            <div className="space-y-2">
              {cat.top_3.map(t => {
                const mc = { 1: '#F59E0B', 2: '#9CA3AF', 3: '#CD7F32' };
                return (
                  <div key={t.rank} className="flex items-center justify-between px-3 py-2 rounded-lg"
                    style={{ backgroundColor: t.is_mine ? '#F5831F08' : '#F8F7F4', border: t.is_mine ? '1px solid #F5831F33' : '1px solid transparent' }}>
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black text-white" style={{ backgroundColor: mc[t.rank] || '#CCC' }}>{t.rank}</span>
                      <span className="text-xs font-medium" style={{ color: t.is_mine ? '#F5831F' : '#1A1209' }}>{t.display_name}</span>
                    </div>
                    <span className="text-xs font-bold" style={{ color: '#666' }}>{t.value} {cat.unit}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <p className="text-[10px] text-center mt-4" style={{ color: '#CCC' }}>
        Updated {new Date(leaderboardData.updated_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
      </p>
    </div>
  );
};

export default LeaderboardTab;
